import type { FrameMessage } from "./frame-messages";
import type { BuyerSession, CheckoutEvent } from "./state-machine";
import type {
  Clock,
  EnginePort,
  EventsPort,
  IdentityPort,
  PaymentAccount,
  PaymentsPort,
  TimerHandle,
} from "./ports";
import type { CoreConfig, CoreState } from "./state";

/**
 * What the core's flows share. A flow reads two kinds of state, and the
 * difference is deliberate: `view` is the state as it was when the buyer
 * pressed (or when a reaction ran) — what a component's closure saw — and
 * `live()` is the state now, for the checks the component made through refs
 * and functional updates. Keeping both is what keeps the flows' timing and
 * outcomes exactly as they were inside the component.
 */

export type CheckoutCoreDeps = {
  engine: EnginePort;
  identity: IdentityPort;
  payments: PaymentsPort;
  events: EventsPort;
  clock: Clock;
  /** The session is over: forget its client secret wherever it was kept. */
  forgetSecret?: () => void;
};

export type StatePatch =
  | Partial<CoreState>
  | ((state: CoreState) => Partial<CoreState>);

/** What the component kept in refs: facts that never re-render anything. */
export type CoreRefs = {
  emitted: Set<string>;
  /** An attestation is in flight; nothing else starts one. */
  attesting: boolean;
  /** The buyer took the chip off: their browser is still signed in, but
   *  this checkout no longer speaks for it until they say so. */
  dismissed: boolean;
  hostCodeAsked: boolean;
  setupAsked: boolean;
  /** The host's first answer has been asked for. */
  hostTried: boolean;
  /** That answer: the token, or null when the host had none. */
  hostToken: Promise<string | null> | null;
  /** It has been shown to the engine (once the cart is open). */
  hostChecked: boolean;
  askedToRemember: boolean;
  /** The address last asked about: is this a Lissen buyer's? */
  enrolmentFor: string | null;
  /** Email writes, in order: the last submitted address is the last write. */
  emailWrites: Promise<void>;
  savedEmail: string | null;
  emailRevision: number;
  completing: boolean;
  debounce: TimerHandle | null;
  /** A new card is being set up; a second press waits. */
  submittingCard: boolean;
  bankAnswered: (() => void) | null;
  paymentIntentId: string;
  /** The method that paid, to tell whether it joined the wallet. */
  paidWith: string | null;
  /** The organizer's account, once the engine has named it (in the wallet
   *  setup answer at page open, or a pay answer): a device session for the
   *  engine's confirmation is made there. */
  paymentAccount: PaymentAccount | null;
};

/** What a read of the session answers with, once it is applied. */
export type SessionEvent = Extract<CheckoutEvent, { session: BuyerSession }>;

export type CoreContext = {
  deps: CheckoutCoreDeps;
  config: CoreConfig;
  live: () => CoreState;
  set: (patch: StatePatch) => void;
  dispatch: (event: CheckoutEvent) => void;
  /** A read of the session is being made: its answer is dispatched through
   *  the function returned, which applies it only if no read made after it
   *  has been applied already, and says whether it did (checkout-core.ts). */
  sessionRead: () => (event: SessionEvent) => boolean;
  /** A write to the session is being sent: its answer is dispatched through
   *  the function returned, always, and outranks every read made before. */
  sessionWrite: () => (event: SessionEvent) => void;
  /** Settles once the checkout first has its session. */
  loaded: Promise<void>;
  emit: (message: FrameMessage) => void;
  emitOnce: (key: string, message: FrameMessage) => void;
  refs: CoreRefs;
};

export function initialRefs(): CoreRefs {
  return {
    emitted: new Set(),
    attesting: false,
    dismissed: false,
    hostCodeAsked: false,
    setupAsked: false,
    hostTried: false,
    hostToken: null,
    hostChecked: false,
    askedToRemember: false,
    enrolmentFor: null,
    emailWrites: Promise.resolve(),
    savedEmail: null,
    emailRevision: 0,
    completing: false,
    debounce: null,
    submittingCard: false,
    bankAnswered: null,
    paymentIntentId: "",
    paidWith: null,
    paymentAccount: null,
  };
}
